import Hex from './hex';

export default class DropShadow {
	public get blur(): number {
		return this.#blur;
	}

	public get color(): Hex {
		return this.#color;
	}

	public get spread(): number {
		return this.#spread;
	}

	public get x(): number {
		return this.#x;
	}

	public get y(): number {
		return this.#y;
	}

	#blur: number;
	#color: Hex;
	#spread: number;
	#x: number;
	#y: number;

	/**
	 * The box-shadow CSS property adds shadow effects around an element's frame. You can set multiple effects separated by commas. A box shadow is described by X and Y offsets relative to the element, blur and spread radius, and color.
	 *
	 * @param x The x offset in pixels
	 * @param y The y offset in pixels
	 * @param blur The blur in pixels
	 * @param spread The spread in pixels
	 * @param color An instance of the Hex class
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/box-shadow} for syntax
	 */
	public constructor(x: number, y: number, blur: number, spread: number, color: Hex) {
		assertArguments(x, y, blur, spread, color);
		this.#x = x;
		this.#y = y;
		this.#spread = spread;
		this.#blur = blur;
		this.#color = color;
	}

	/**
	 * Return the DropShadow value as a 'Npx Npx Npx Npx #RRGGBBAA' format string
	 *
	 * @returns String
	 */
	public toStyleString(): string {
		return `${this.#x}px ${this.#y}px ${this.#blur}px ${this.#spread}px ${this.#color.toStyleString()}`;
	}
}

function assertArguments(x: number, y: number, blur: number, spread: number, color: Hex): void {
	assertIsNumber(x);
	assertIsNumber(y);
	assertIsNonNegative(blur);
	assertIsNumber(spread);
	assertIsInstanceOfHex(color);
}

function assertIsInstanceOfHex(value: unknown): asserts value is Hex {
	if (value instanceof Hex) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of Hex`);
}

function assertIsNonNegative(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

function assertIsNumber(value: unknown): asserts value is number {
	if (typeof value === 'number') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type number`);
}
